import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  Calendar,
  CreditCard,
  ShieldCheck,
  Upload,
  CheckCircle2,
  DollarSign,
  ArrowRight,
  Users,
  BarChart3,
  Rocket,
  MapPin,
  LayoutGrid,
  Headphones,
  ExternalLink,
  Star,
  ThumbsUp
} from 'lucide-react';
import Navbar from '../components/Navbar';

// Paleta de colores Dark UI
const BG_DARK = '#030B1A'; 
const CARD_BG = '#0A1526';
const CARD_BORDER = 'rgba(255, 255, 255, 0.08)';
const ACCENT_BLUE = '#134168';
const ACCENT_CYAN = '#36C0C7';
const SECTION_BG = '#F4F8FC';

export default function ProvidersLanding() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const goRegister = () => navigate('/register');

  const catImg = (type) => `${process.env.PUBLIC_URL}/images/categories/${type}.jpg`;
  const CATEGORIES = [
    { type: 'jet_ski', img: catImg('jet_ski'), label: t('providersLanding.catJetSki', 'Jet Skis'), desc: t('providersLanding.catJetSkiDesc', 'Adrenalina sobre el agua') },
    { type: 'boat', img: catImg('boat'), label: t('providersLanding.catBoat', 'Lanchas'), desc: t('providersLanding.catBoatDesc', 'Paseos, pesca y fiestas en el mar') },
    { type: 'atv', img: catImg('atv'), label: t('providersLanding.catAtv', 'ATVs / Buggies'), desc: t('providersLanding.catAtvDesc', 'Aventura todoterreno') },
    { type: 'motorcycle', img: catImg('motorcycle'), label: t('providersLanding.catMotorcycle', 'Motocicletas'), desc: t('providersLanding.catMotorcycleDesc', 'Recorre ciudad y costa') },
    { type: 'scooter', img: catImg('scooter'), label: t('providersLanding.catScooter', 'Scooters'), desc: t('providersLanding.catScooterDesc', 'Movilidad fácil y económica') },
  ];

  const SERVICES = [
    { icon: Upload, title: t('providersLanding.service1Title', 'Publica tu vehículo'), desc: t('providersLanding.service1Desc', 'Sube fotos, establece tus precios y define tus reglas.') },
    { icon: Calendar, title: t('providersLanding.service2Title', 'Gestiona tus reservas'), desc: t('providersLanding.service2Desc', 'Acepta o rechaza solicitudes desde tu panel de control.') },
    { icon: CreditCard, title: t('providersLanding.service3Title', 'Recibe pagos seguros'), desc: t('providersLanding.service3Desc', 'El dinero va directo a tu cuenta sin complicaciones.') },
    { icon: ShieldCheck, title: t('providersLanding.service4Title', 'Soporte y protección'), desc: t('providersLanding.service4Desc', 'Estamos contigo en cada paso para tu tranquilidad.') },
  ];

  const STEPS = [
    { icon: Users, title: t('providersLanding.step1Title', 'Crea tu cuenta'), desc: t('providersLanding.step1Desc', 'Regístrate en menos de 2 minutos sin costo alguno.') },
    { icon: Upload, title: t('providersLanding.step2Title', 'Sube tu anuncio'), desc: t('providersLanding.step2Desc', 'Añade los detalles y fotos de tu vehículo.') },
    { icon: Calendar, title: t('providersLanding.step3Title', 'Recibe clientes'), desc: t('providersLanding.step3Desc', 'Los usuarios verificados podrán reservar tu vehículo.') },
    { icon: DollarSign, title: t('providersLanding.step4Title', 'Empieza a ganar'), desc: t('providersLanding.step4Desc', 'Recibe tus pagos de forma segura tras cada reserva.') },
  ];

  return (
    <div className="min-h-screen bg-white text-[#0F172A] font-sans">
      <Navbar />

      {/* HERO SECTION */}
      <section
  className="relative overflow-hidden pt-40 pb-10 md:pt-48 md:pb-16"
  style={{
    backgroundColor: BG_DARK
  }}
>
  {/* Imagen de fondo */}
  {/* Imagen de fondo */}
<img
  src={`${process.env.PUBLIC_URL}/foto2.jpg`}
  alt="Fondo"
  className="absolute inset-0 w-full h-full object-cover"
/>

{/* Degradado principal */}
<div
  className="absolute inset-0"
  style={{
    background: `
      linear-gradient(
        to right,
        rgba(3,11,26,1) 0%,
        rgba(3,11,26,0.92) 35%,
        rgba(3,11,26,0.75) 60%,
        rgba(3,11,26,0.85) 80%,
        rgba(3,11,26,0.98) 100%
      )
    `
  }}
/>

{/* Brillo azul */}
<div
  className="absolute inset-0"
  style={{
    background:
      'radial-gradient(circle at 65% 30%, rgba(0,132,255,0.18), transparent 45%)'
  }}
/>

  
        <div className="relative container mx-auto px-6 md:px-12 max-w-[1400px]">
          
          {/* Línea decorativa brillante a la izquierda */}
          <div className="absolute left-4 top-10 bottom-10 w-[2px] hidden md:block" style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,163,255,0.5), transparent)' }}>
            <div className="absolute top-1/4 -left-[3px] w-2 h-2 rounded-full bg-[#36C0C7] shadow-[0_0_12px_#36C0C7]"></div>
            <div className="absolute bottom-1/4 -left-[3px] w-2 h-2 rounded-full bg-[#36C0C7] shadow-[0_0_12px_#36C0C7]"></div>
          </div>

          <div className="grid lg:grid-cols-[1fr_auto] gap-12 lg:gap-20 items-center md:pl-10">
            
            {/* LEFT COLUMN: Text & CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-white z-10 max-w-2xl"
            >
              {/* Badge Plataforma #1 */}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#1E3048] bg-[#0F1C33]/60 text-xs font-semibold tracking-wide mb-8 text-gray-200 backdrop-blur-md">
                <Rocket className="w-3.5 h-3.5 text-[#36C0C7]" />
                {t('providersLanding.platformBadge')}
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] mb-6 break-words">
              {t('providersLanding.heroTitlePart1')}
              <br />
              <span style={{ color: ACCENT_CYAN }}>
                {t('providersLanding.heroTitleHighlight')}
              </span>
            </h1>
              
              <p className="text-lg text-gray-300 mb-10 leading-relaxed font-light max-w-lg drop-shadow-md">
                {t('providersLanding.heroSubtitle')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl font-semibold transition-all text-white border border-[#2A3E59] bg-[#0A1526]/80 hover:bg-[#14243D] backdrop-blur-md"
                >
                  {t('providersLanding.ctaMarketplace')}
                  <ExternalLink className="w-4 h-4 text-gray-400" />
                </Link>
              </div>

              {/* Trust Badges under buttons */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-gray-300">
                <span className="inline-flex items-center gap-2 drop-shadow-md">
                  <div className="rounded-full bg-[#36C0C7] text-white p-0.5">
                    <CheckCircle2 className="w-4 h-4" />
                  </div>
                  {t('providersLanding.noMembershipFee')}
                </span>
                <span className="inline-flex items-center gap-2 drop-shadow-md">
                  <div className="rounded-full bg-[#36C0C7] text-white p-0.5">
                    <CheckCircle2 className="w-4 h-4" />
                  </div>
                  {t('providersLanding.securePayments')}
                </span>
                <span className="inline-flex items-center gap-2 drop-shadow-md">
                  <div className="rounded-full bg-[#36C0C7] text-white p-0.5">
                    <CheckCircle2 className="w-4 h-4" />
                  </div>
                  {t('providersLanding.support247')}
                </span>
              </div>
            </motion.div>

            {/* RIGHT COLUMN: KPI Glass Card */}
            <motion.div
  
  style={{ marginTop: '100px' }}
>
              <div 
                className="rounded-3xl overflow-hidden shadow-[0_10px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl"
                style={{
                  background: 'rgba(10, 21, 38, 0.45)', 
                  border: `1px solid ${CARD_BORDER}`,
                }}
              >
                <div className="grid grid-cols-2 p-8 gap-y-10 relative">
                  {/* Bordes internos sutiles */}
                  <div className="absolute top-1/2 left-8 right-8 h-px bg-white/10 -translate-y-1/2"></div>
                  <div className="absolute left-1/2 top-8 bottom-8 w-px bg-white/10 -translate-x-1/2"></div>

                  {/* Zonas Activas */}
                  <div className="text-white pr-4">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/30 flex items-center justify-center mb-4 border border-blue-500/40">
                      <MapPin className="w-5 h-5 text-blue-300" />
                    </div>
                    <p className="text-4xl font-bold mb-1 tracking-tight">12+</p>
                    <p className="text-sm text-gray-300 font-medium">{t('providersLanding.activeZones')}</p>
                  </div>

                  {/* Categorías */}
                  <div className="text-white pl-6">
                    <div className="w-10 h-10 rounded-xl bg-indigo-500/30 flex items-center justify-center mb-4 border border-indigo-500/40">
                      <LayoutGrid className="w-5 h-5 text-indigo-300" />
                    </div>
                    <p className="text-4xl font-bold mb-1 tracking-tight">5</p>
                    <p className="text-sm text-gray-300 font-medium">{t('providersLanding.assetCategories')}</p>
                  </div>

                  {/* Soporte */}
                  <div className="text-white pr-4 mt-2">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/30 flex items-center justify-center mb-4 border border-emerald-500/40">
                      <Headphones className="w-5 h-5 text-emerald-300" />
                    </div>
                    <p className="text-4xl font-bold mb-1 tracking-tight">24/7</p>
                    <p className="text-sm text-gray-300 font-medium">{t('providersLanding.whatsappSupport')}</p>
                  </div>

                  {/* Costo */}
                  <div className="text-white pl-6 mt-2">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/30 flex items-center justify-center mb-4 border border-amber-500/40">
                      <DollarSign className="w-5 h-5 text-amber-300" />
                    </div>
                    <p className="text-4xl font-bold mb-1 tracking-tight">0$</p>
                    <p className="text-sm text-gray-300 font-medium">{t('providersLanding.publicationCost')}</p>
                  </div>
                </div>

                {/* Banner inferior dentro de la tarjeta */}
                <div className="bg-[#050E1D]/80 backdrop-blur-md p-5 border-t border-white/10 flex items-center gap-4">
                  <div className="w-8 h-8 rounded-lg bg-[#14253D] flex items-center justify-center shrink-0">
                    <BarChart3 className="w-4 h-4 text-[#36C0C7]" />
                  </div>
                  <p className="text-xs text-gray-300 leading-relaxed">
                  {t('providersLanding.onlinePlatform')}{' '}
                  <span className="text-[#36C0C7] font-semibold">
                    {t('providersLanding.onlinePlatformHighlight')}
                  </span>
                  : {t('providersLanding.onlinePlatformDesc')}
                </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* BOTTOM TRUST STRIP */}
          <div className="mt-20 z-20 relative">
            <div 
              className="rounded-2xl flex flex-col md:flex-row md:items-center justify-between p-6 md:px-10 gap-6 backdrop-blur-xl shadow-[0_10px_30px_rgba(0,0,0,0.3)]"
              style={{
                background: 'rgba(10, 21, 38, 0.7)',
                border: `1px solid ${CARD_BORDER}`,
              }}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center border border-blue-500/30 shrink-0">
                  <ShieldCheck className="w-6 h-6 text-[#36C0C7]" />
                </div>
                <div>
                  <h4 className="text-white font-bold text-sm md:text-base">{t('providersLanding.secureTransactions')}</h4>
                  <p className="text-gray-400 text-sm">{t('providersLanding.withAzul')}</p>
                </div>
              </div>

              <div className="hidden md:block w-px h-10 bg-white/10"></div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center border border-blue-500/30 shrink-0">
                  <Star className="w-6 h-6 text-[#36C0C7]" />
                </div>
                <div>
                  <h4 className="text-white font-bold text-sm md:text-base">{t('providersLanding.verifiedProviders')}</h4>
                  <p className="text-gray-400 text-sm">{t('providersLanding.trusted100')}</p>
                </div>
              </div>

              <div className="hidden md:block w-px h-10 bg-white/10"></div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center border border-blue-500/30 shrink-0">
                  <ThumbsUp className="w-6 h-6 text-[#36C0C7]" />
                </div>
                <div>
                  <h4 className="text-white font-bold text-sm md:text-base">{t('providersLanding.thousandsBookings')}</h4>
                  <p className="text-gray-400 text-sm">{t('providersLanding.growingDaily')}</p>
                </div>
              </div>

              <div className="hidden lg:block w-px h-10 bg-white/10"></div>

              <div className="flex flex-col gap-2">
                <div className="flex -space-x-3">
                  <img className="w-8 h-8 rounded-full border-2 border-[#0A1526]" src="https://i.pravatar.cc/100?img=33" alt="User" />
                  <img className="w-8 h-8 rounded-full border-2 border-[#0A1526]" src="https://i.pravatar.cc/100?img=47" alt="User" />
                  <img className="w-8 h-8 rounded-full border-2 border-[#0A1526]" src="https://i.pravatar.cc/100?img=12" alt="User" />
                  <img className="w-8 h-8 rounded-full border-2 border-[#0A1526]" src="https://i.pravatar.cc/100?img=32" alt="User" />
                  <div className="w-8 h-8 rounded-full border-2 border-[#0A1526] bg-[#36C0C7] flex items-center justify-center text-[10px] font-bold text-white z-10">
                    +2K
                  </div>
                </div>
                <p className="text-gray-400 text-xs max-w-[180px]">{t('providersLanding.joinProviders')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICIOS */}
      <section className="py-20" style={{ background: '#FFFFFF' }}>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-2xl mb-12">
            <p className="text-sm font-bold uppercase tracking-wider mb-3 text-slate-500">
              {t('providersLanding.servicesEyebrow', 'SERVICIOS')}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#030B1A]">
              {t('providersLanding.servicesTitle', 'Todo lo que necesitas para tu negocio')}
            </h2>
            <p className="text-gray-500 text-lg">
              {t('providersLanding.servicesSubtitle', 'Te damos las herramientas para gestionar todo en un solo lugar.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SERVICES.map((s) => (
              <motion.div
                key={s.title}
                whileHover={{ y: -6 }}
                className="rounded-3xl p-8 transition-all"
                style={{ background: SECTION_BG, border: '1px solid #E2EAF4' }}
              >
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{background: "linear-gradient(135deg, #29C7D8 0%, #1E4F8A 100%)"}}>
                  <s.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-[#030B1A]">
                  {s.title}
                </h3>
                <p className="text-gray-500 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORÍAS */}
      <section className="py-20" style={{ background: SECTION_BG }}>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div className="max-w-2xl">
              <p className="text-sm font-bold uppercase tracking-wider mb-3 text-slate-500">
                {t('providersLanding.categoriesEyebrow', 'CATEGORÍAS')}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold mb-3 text-[#030B1A]">
                {t('providersLanding.categoriesTitle', 'Publica cualquier vehículo')}
              </h2>
              <p className="text-gray-500">
                {t('providersLanding.categoriesSubtitle', 'Tenemos una alta demanda en diversas categorías. Empieza hoy mismo.')}
              </p>
            </div>
            <Link to="/" className="inline-flex items-center gap-2 font-bold hover:underline text-[#134168]">
              {t('providersLanding.categoriesCta', 'Ver categorías')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* BUG-003 (ISO 25010 Fase 2): 3 columnas en tablet para que las descripciones se lean completas */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {CATEGORIES.map((c) => (
              <Link
                key={c.type}
                to={`/search?type=${c.type}`}
                className="group rounded-2xl overflow-hidden bg-white transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
                style={{ border: '1px solid #E2EAF4' }}
              >
                <div className="relative h-36 overflow-hidden">
                  <img
                    src={c.img}
                    alt={c.label}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
                  <h3 className="absolute bottom-3 left-4 right-4 text-white font-bold text-lg drop-shadow-md">
                    {c.label}
                  </h3>
                </div>
                <div className="p-4">
                  <p className="text-xs text-[#5A6B82] leading-snug mb-2">
                    {c.desc}
                  </p>
                  <span className="text-xs font-semibold text-[#134168] inline-flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                    {t('providersLanding.categoriesHover', 'Ver más →')}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-sm font-bold uppercase tracking-wider mb-3 text-slate-500">
              {t('providersLanding.stepsEyebrow', 'CÓMO FUNCIONA')}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#030B1A]">
              {t('providersLanding.stepsTitle', 'Cuatro simples pasos')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {STEPS.map((step, idx) => (
              <div key={step.title} className="relative">
                <div className="rounded-3xl p-6 h-full" style={{ background: SECTION_BG, border: '1px solid #E2EAF4' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white bg-[#134168]">
                      {idx + 1}
                    </span>
                    <step.icon className="w-6 h-6 text-[#030B1A]" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-[#030B1A]">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section
        className="py-20"
        style={{
          background: `linear-gradient(135deg, ${BG_DARK} 0%, #01040A 100%)`,
        }}
      >
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center text-white">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            {t('providersLanding.ctaFinalTitle', '¿Listo para empezar a ganar?')}
          </h2>
          <p className="text-lg md:text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            {t('providersLanding.ctaFinalSubtitle', 'Únete a la red más grande de proveedores de vehículos en RD.')}
          </p>
          <div className="flex justify-center">
            <Link
              to="/contacto"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-all"
            >
              {t('providersLanding.ctaFinalSecondary', 'Contactar Ventas')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}